/**
 * pageNavMap — declarative route → breadcrumbs + suggested-next config.
 *
 * Stories the platform tells:
 *
 *   The Discovery story:
 *     / → /intelligence → /atlas → /marketplace
 *
 *   The Intelligence story:
 *     /intelligence → /intelligence/explore → /intelligence/opportunities
 *      → /intelligence/capital → /intelligence/markets → /intelligence/graphql
 *
 *   The Investor story:
 *     /for/investors → /asset-exchange → /portal
 *
 *   The Build story:
 *     /ai-tools/waste-to-resource → /ai-tools/hub-builder → /ai-tools/digital-twin
 *
 *   The Trust story:
 *     /methodology → /network-proof → /credits → /dpp
 *
 * Each entry is `{ breadcrumbs: [{label, to?}], next: {label, to}, back?, hidden? }`.
 */

const HOME = { label: "Home", to: "/" };
const INTEL = { label: "Intelligence", to: "/intelligence" };
const ATLAS = { label: "Atlas", to: "/atlas" };
const PLATFORM = { label: "Platform" }; // non-clickable parent

// Order-sensitive lookup. The first matcher wins, so put exact paths first
// and dynamic prefixes last.
const ENTRIES = [
  // Landing
  ["/", { hidden: true }],

  // ─── INTELLIGENCE STORY ───────────────────────────────────────────────
  ["/intelligence", {
    breadcrumbs: [INTEL],
    next: { label: "Atlas", to: "/atlas" },
  }],
  ["/intelligence/explore", {
    breadcrumbs: [INTEL, { label: "Knowledge Graph" }],
    next: { label: "Opportunities", to: "/intelligence/opportunities" },
  }],
  ["/intelligence/opportunities", {
    breadcrumbs: [INTEL, { label: "Opportunities" }],
    next: { label: "Capital", to: "/intelligence/capital" },
  }],
  ["/intelligence/capital", {
    breadcrumbs: [INTEL, { label: "Capital" }],
    next: { label: "Markets", to: "/intelligence/markets" },
  }],
  ["/intelligence/communities", {
    breadcrumbs: [INTEL, { label: "Communities" }],
    next: { label: "Materials", to: "/intelligence/materials" },
  }],
  ["/intelligence/materials", {
    breadcrumbs: [INTEL, { label: "Materials" }],
    next: { label: "Technologies", to: "/intelligence/technologies" },
  }],
  ["/intelligence/technologies", {
    breadcrumbs: [INTEL, { label: "Technologies" }],
    next: { label: "Capital", to: "/intelligence/capital" },
  }],
  ["/intelligence/markets", {
    breadcrumbs: [INTEL, { label: "Markets" }],
    next: { label: "GraphQL API", to: "/intelligence/graphql" },
  }],
  ["/intelligence/graphql", {
    breadcrumbs: [INTEL, { label: "GraphQL API" }],
    next: { label: "Atlas", to: "/atlas" },
  }],

  // ─── ATLAS / DISCOVERY ────────────────────────────────────────────────
  ["/atlas", {
    breadcrumbs: [PLATFORM, { label: "Atlas" }],
    next: { label: "Marketplace", to: "/marketplace" },
  }],

  // ─── MARKETPLACE / NETWORK ────────────────────────────────────────────
  ["/marketplace", {
    breadcrumbs: [PLATFORM, { label: "Marketplace" }],
    next: { label: "Hubs", to: "/hubs" },
  }],
  ["/hubs", {
    breadcrumbs: [PLATFORM, { label: "Hubs" }],
    next: { label: "Network Proof", to: "/network-proof" },
  }],
  ["/network", {
    breadcrumbs: [PLATFORM, { label: "Network" }],
    next: { label: "Marketplace", to: "/marketplace" },
  }],
  ["/network-proof", {
    breadcrumbs: [PLATFORM, { label: "Network Proof" }],
    next: { label: "Credits", to: "/credits" },
  }],
  ["/credits", {
    breadcrumbs: [PLATFORM, { label: "Credits" }],
    next: { label: "Methodology", to: "/methodology" },
  }],
  ["/dpp", {
    breadcrumbs: [PLATFORM, { label: "Product Passports" }],
    next: { label: "Methodology", to: "/methodology" },
  }],

  // ─── INVESTOR STORY ───────────────────────────────────────────────────
  ["/for/investors", {
    breadcrumbs: [{ label: "For" }, { label: "Investors" }],
    next: { label: "Asset Exchange", to: "/asset-exchange" },
  }],
  ["/asset-exchange", {
    breadcrumbs: [{ label: "Investors", to: "/for/investors" }, { label: "Asset Exchange" }],
    next: { label: "Investor Portal", to: "/portal" },
  }],
  ["/portal", {
    breadcrumbs: [{ label: "Investors", to: "/for/investors" }, { label: "Portal" }],
    next: { label: "Network Proof", to: "/network-proof" },
  }],
  ["/for/corporates", {
    breadcrumbs: [{ label: "For" }, { label: "Corporates" }],
    next: { label: "Hubs", to: "/hubs" },
  }],
  ["/for/governments", {
    breadcrumbs: [{ label: "For" }, { label: "Governments" }],
    next: { label: "Methodology", to: "/methodology" },
  }],
  ["/for/fintech", {
    breadcrumbs: [{ label: "For" }, { label: "Fintech" }],
    next: { label: "Asset Exchange", to: "/asset-exchange" },
  }],
  ["/for/landowners", {
    breadcrumbs: [{ label: "For" }, { label: "Landowners" }],
    next: { label: "Foundation", to: "/foundation" },
  }],

  // ─── AI TOOLS STORY ───────────────────────────────────────────────────
  ["/ai-tools/waste-to-resource", {
    breadcrumbs: [{ label: "AI Tools" }, { label: "Waste-to-Resource" }],
    next: { label: "Hub Builder", to: "/ai-tools/hub-builder" },
  }],
  ["/ai-tools/hub-builder", {
    breadcrumbs: [{ label: "AI Tools" }, { label: "Hub Builder" }],
    next: { label: "Opportunity Explorer", to: "/ai-tools/opportunity-explorer" },
  }],
  ["/ai-tools/opportunity-explorer", {
    breadcrumbs: [{ label: "AI Tools" }, { label: "Opportunity Explorer" }],
    next: { label: "Digital Twin", to: "/ai-tools/digital-twin" },
  }],
  ["/ai-tools/digital-twin", {
    breadcrumbs: [{ label: "AI Tools" }, { label: "Digital Twin" }],
    next: { label: "Calculators", to: "/ai-tools/calculators" },
  }],
  ["/ai-tools/calculators", {
    breadcrumbs: [{ label: "AI Tools" }, { label: "Calculators" }],
    next: { label: "Atlas", to: "/atlas" },
  }],

  // ─── COMMUNITY / TRUST ────────────────────────────────────────────────
  ["/community", {
    breadcrumbs: [{ label: "Community" }],
    next: { label: "People", to: "/people" },
  }],
  ["/people", {
    breadcrumbs: [{ label: "Community", to: "/community" }, { label: "People" }],
    next: { label: "Foundation", to: "/foundation" },
  }],
  ["/foundation", {
    breadcrumbs: [{ label: "Community", to: "/community" }, { label: "Foundation" }],
    next: { label: "Academy", to: "/academy" },
  }],
  ["/academy", {
    breadcrumbs: [{ label: "Community", to: "/community" }, { label: "Academy" }],
    next: { label: "About", to: "/about" },
  }],
  ["/about", {
    breadcrumbs: [{ label: "About" }],
    next: { label: "Methodology", to: "/methodology" },
  }],
  ["/methodology", {
    breadcrumbs: [{ label: "Methodology" }],
    next: { label: "Contact", to: "/contact" },
  }],
  ["/contact", {
    breadcrumbs: [{ label: "Contact" }],
    next: { label: "Atlas", to: "/atlas" },
  }],

  // ─── ADMIN ────────────────────────────────────────────────────────────
  ["/admin", {
    breadcrumbs: [{ label: "Admin" }],
    next: { label: "Intelligence CMS", to: "/admin/intelligence" },
  }],
  ["/admin/intelligence", {
    breadcrumbs: [{ label: "Admin", to: "/admin" }, { label: "Intelligence CMS" }],
    next: { label: "Atlas", to: "/atlas" },
  }],
  ["/admin/health", {
    breadcrumbs: [{ label: "Admin", to: "/admin" }, { label: "Health" }],
  }],
  ["/admin/design-system", {
    breadcrumbs: [{ label: "Admin", to: "/admin" }, { label: "Design System" }],
    next: { label: "Intelligence CMS", to: "/admin/intelligence" },
  }],
  ["/circularity-station-operating-system", {
    breadcrumbs: [{ label: "Investor Brief" }, { label: "Station Operating System" }],
    back: { label: "Home", to: "/" },
    next: { label: "Intelligence Platform", to: "/intelligence" },
  }],
  ["/dashboard", {
    breadcrumbs: [{ label: "Dashboard" }],
    next: { label: "Atlas", to: "/atlas" },
  }],

  // ─── AUTH ─────────────────────────────────────────────────────────────
  ["/login", { hidden: true }],
  ["/signup", { hidden: true }],
  ["/forgot-password", { hidden: true }],
];

// Prefix-based fallbacks for dynamic routes (passports, etc.)
const PREFIX_FALLBACKS = [
  ["/intelligence/community/", (path) => ({
    breadcrumbs: [INTEL, { label: "Communities", to: "/intelligence/communities" }, { label: path.split("/").pop() }],
    back: { label: "All communities", to: "/intelligence/communities" },
    next: { label: "Materials", to: "/intelligence/materials" },
  })],
  ["/intelligence/material/", (path) => ({
    breadcrumbs: [INTEL, { label: "Materials", to: "/intelligence/materials" }, { label: path.split("/").pop() }],
    back: { label: "All materials", to: "/intelligence/materials" },
    next: { label: "Technologies", to: "/intelligence/technologies" },
  })],
  ["/intelligence/technology/", (path) => ({
    breadcrumbs: [INTEL, { label: "Technologies", to: "/intelligence/technologies" }, { label: path.split("/").pop() }],
    back: { label: "All technologies", to: "/intelligence/technologies" },
    next: { label: "Capital", to: "/intelligence/capital" },
  })],
  ["/intelligence/capital/passport/", (path) => ({
    breadcrumbs: [INTEL, { label: "Capital", to: "/intelligence/capital" }, { label: path.split("/").pop() }],
    back: { label: "Capital ecosystem", to: "/intelligence/capital" },
    next: { label: "Opportunities", to: "/intelligence/opportunities" },
  })],
  ["/intelligence/opportunity/", (path) => ({
    breadcrumbs: [INTEL, { label: "Opportunities", to: "/intelligence/opportunities" }, { label: path.split("/").pop() }],
    back: { label: "All opportunities", to: "/intelligence/opportunities" },
    next: { label: "Markets", to: "/intelligence/markets" },
  })],
  ["/intelligence/capital/", (path) => ({
    breadcrumbs: [INTEL, { label: "Capital" }, { label: path.split("/").pop() }],
    next: { label: "Opportunities", to: "/intelligence/opportunities" },
  })],
  ["/intelligence/explore/", (_path) => ({
    breadcrumbs: [INTEL, { label: "Knowledge Graph", to: "/intelligence/explore" }, { label: "Detail" }],
    back: { label: "Knowledge Graph", to: "/intelligence/explore" },
    next: { label: "Opportunities", to: "/intelligence/opportunities" },
  })],
  ["/intelligence/indexes/", (_path) => ({
    breadcrumbs: [INTEL, { label: "Markets", to: "/intelligence/markets" }, { label: "Index" }],
    back: { label: "Markets", to: "/intelligence/markets" },
    next: { label: "GraphQL API", to: "/intelligence/graphql" },
  })],
  ["/dpp/", (_path) => ({
    breadcrumbs: [PLATFORM, { label: "Product Passports", to: "/dpp" }, { label: "Detail" }],
    back: { label: "All passports", to: "/dpp" },
  })],
  ["/credits/", (_path) => ({
    breadcrumbs: [PLATFORM, { label: "Credits", to: "/credits" }, { label: "Detail" }],
    back: { label: "All credits", to: "/credits" },
  })],
];

const EXACT_MAP = Object.fromEntries(ENTRIES);

export function getPageNavConfig(pathname) {
  if (EXACT_MAP[pathname]) return EXACT_MAP[pathname];
  for (const [prefix, fn] of PREFIX_FALLBACKS) {
    if (pathname.startsWith(prefix)) return fn(pathname);
  }
  // Default fallback — show Back + Home but no breadcrumb or next.
  return { breadcrumbs: [], back: undefined, next: undefined };
}
